import { assertNever } from '@solinkcorp/utils';
import { type ImpersonatorSession, type UserSession } from '../types';
import { type AuthAction } from './authenticationActions';

export interface AuthenticationState {
  /**
   * "loading" starts out true, and becomes false once we've worked out if there's
   * already a user logged in or not.
   */
  loading: boolean;
  /** The currently logged in user. */
  // Note that there's some derived information in here - most of this is
  // derived from the jwtToken.  Normally I'd advocate to memoize the derived
  // state, but it adds a lot of complexity here, and it's not at all hard to
  // keep everything in here in sync.
  userSession?: UserSession;
  /** The currently logged in impersonator. */
  impersonator?: ImpersonatorSession;
  isPublicSession?: boolean;
}

export const INITIAL_AUTHENTICATION_STATE = {
  loading: true,
};

function deepFreeze<T extends object>(obj: T): T {
  for (const key of Object.keys(obj)) {
    const value = (obj as any)[key];
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      deepFreeze(value as Record<string, unknown>);
    }
  }

  Object.freeze(obj);
  return obj;
}

/**
 * Reducer= for the AuthenticationProvider.
 *
 * Note that state for this reducer is NOT stored in redux - instead we're using
 * `React.useReducer()` in the AuthenticationProvider.  Also note that this reducer
 * isn't using immer, since we want to keep AuthenticationProvider light.  Instead
 * we deepFreeze objects ourselves.
 */
export function authenticationStateReducer(
  state: AuthenticationState,
  action: AuthAction,
): AuthenticationState {
  let newState = state;

  switch (action.type) {
    case 'setInitialState':
      if (action.loggingOut) {
        // If the user is logging out, leave the `loading` flag set true.  The
        // page will reload when the logout is complete, and we'll end up here
        // again.
        newState = INITIAL_AUTHENTICATION_STATE;
      } else {
        newState = {
          loading: false,
          impersonator: action.impersonatorSession,
          userSession: action.userSession,
        };
      }
      break;

    case 'login':
    case 'refresh': {
      const session = action.userSession;
      switch (session.type) {
        case 'callHome':
        case 'guest':
          newState = { ...state, userSession: session };
          break;
        case 'impersonator':
          newState = { ...state, impersonator: session };
          break;
        default:
          assertNever(session);
      }
      break;
    }

    case 'impersonate':
      newState = { ...state, userSession: action.userSession };
      break;

    case 'refreshImpersonated':
      newState = { ...state, impersonator: action.impersonator, userSession: action.userSession };
      break;

    case 'setPreferences':
      if (state.userSession) {
        newState = {
          ...state,
          userSession: {
            ...state.userSession,
            profile: {
              ...state.userSession.profile,
              preferences: action.preferences,
            },
          },
        };
      }
      break;

    default:
      assertNever(action);
  }

  return deepFreeze(newState);
}
